/**
 * The app's information architecture, defined once.
 *
 * The sidebar draws from this tree, so there is one answer to what exists, what it is called and
 * who may see it. Page titles in the top bar come from it too. The spaces (docs/refactor/05-navegacao.md):
 *
 *   Início · Caixa de entrada · Agentes · Projeto · Acompanhar · Configurações
 *
 * Labels are translation keys; each screen renders them with `t()`. Icons are names, resolved to
 * lucide components by `NAV_ICONS`, so the tree stays plain data that a test can read.
 */

import type { LucideIcon } from 'lucide-react'
import {
  BookOpen,
  Bot,
  Bug,
  CalendarDays,
  ChartNoAxesColumn,
  Cpu,
  FilePen,
  FileText,
  FolderGit2,
  Gauge,
  Hammer,
  History,
  Inbox,
  LayoutDashboard,
  LayoutGrid,
  ListChecks,
  MessagesSquare,
  Pin,
  PlugZap,
  Puzzle,
  RadioTower,
  ScrollText,
  Settings,
  Shield,
  ShieldCheck,
  Sparkles,
  Telescope,
  Users,
  Workflow,
} from 'lucide-react'
import type { Role } from './types'
import { atLeast } from './auth'

export type NavIconName =
  | 'home' | 'inbox' | 'overview' | 'conversations' | 'build' | 'agents' | 'quality' | 'track' | 'settings'
  | 'editor' | 'docs' | 'pinned' | 'specs' | 'tasks'
  | 'workOrders' | 'connect' | 'remote' | 'repositories' | 'skills' | 'plugins' | 'prompts'
  | 'audit' | 'bugHunt' | 'security'
  | 'live' | 'activity' | 'sessions' | 'reports' | 'usage' | 'myDay'

export const NAV_ICONS: Record<NavIconName, LucideIcon> = {
  home: LayoutDashboard,
  inbox: Inbox,
  overview: LayoutGrid,
  conversations: MessagesSquare,
  build: Hammer,
  agents: Cpu,
  quality: ShieldCheck,
  track: Telescope,
  settings: Settings,
  editor: FilePen,
  docs: BookOpen,
  pinned: Pin,
  specs: FileText,
  tasks: ListChecks,
  workOrders: Workflow,
  connect: PlugZap,
  remote: RadioTower,
  repositories: FolderGit2,
  skills: Sparkles,
  plugins: Puzzle,
  prompts: ScrollText,
  audit: ShieldCheck,
  bugHunt: Bug,
  security: Shield,
  live: Users,
  activity: Bot,
  sessions: History,
  reports: ChartNoAxesColumn,
  usage: Gauge,
  myDay: CalendarDays,
}

/** Who may see an entry. Anything not stated is open to everyone signed in. */
export interface NavRequirement {
  /** The lowest role that sees it. */
  minRole?: Role
  /** Only somebody with a developer profile (their own agents, their own day). */
  developer?: boolean
  /** Only while the organization has the Bug Hunt switched on. */
  bugHunt?: boolean
}

export interface NavItem extends NavRequirement {
  id: string
  to: string
  labelKey: string
  icon: NavIconName
  /** Active only on this exact path, not on the paths under it (`/docs` vs `/docs/pinned`). */
  end?: boolean
  /** In a space with tabs, the tab it is a view of, when several items share one (Qualidade). */
  tab?: string
}

export interface NavArea extends NavRequirement {
  id: string
  labelKey: string
  icon: NavIconName
  /** An area that is one screen: a direct link in the sidebar. With items, they are the screen's tabs. */
  to?: string
  items: NavItem[]
  /** 'header': a menu in the top bar instead of a group in the sidebar (Configurações). */
  placement?: 'header'
  /** Paths that are not items of their own but belong here: a session opened from Sessions, … */
  alsoCovers?: string[]
}

export const NAV_AREAS: NavArea[] = [
  { id: 'home', labelKey: 'nav.dashboard', icon: 'home', to: '/dashboard', items: [] },
  // What waits for the person: work that ended, needs them, failed (docs/refactor/03-caixa-de-entrada.md).
  { id: 'inbox', labelKey: 'inbox.title', icon: 'inbox', to: '/inbox', items: [], developer: true },
  // The spaces of docs/refactor/05-navegacao.md: Agentes, Projeto, Acompanhar, Configurações.
  {
    // The agents, one screen with tabs: first the conversations (the prompt box as the screen), then
    // what runs on its own and commands from elsewhere. Setting one up lives in Configurações (41, A7).
    id: 'agents',
    labelKey: 'nav.area.agents',
    icon: 'agents',
    to: '/agents',
    alsoCovers: ['/onboarding'],
    items: [
      { id: 'conversations', to: '/agents/chat', labelKey: 'nav.conversations', icon: 'conversations' },
      { id: 'workOrders', to: '/agents/work', labelKey: 'nav.workOrders', icon: 'workOrders' },
      { id: 'remote', to: '/agents/remote', labelKey: 'nav.remote', icon: 'remote', developer: true },
    ],
  },
  {
    // Everything that belongs to the project being worked on, one screen with tabs (pages/Space.tsx):
    // its overview, its documents, its quality, its code. One link in the sidebar.
    id: 'project',
    labelKey: 'nav.area.project',
    icon: 'build',
    to: '/project',
    items: [
      { id: 'overview', to: '/project/overview', labelKey: 'nav.overview', icon: 'overview' },
      { id: 'docs', to: '/project/docs', labelKey: 'nav.docs', icon: 'docs' },
      { id: 'pinned', to: '/project/pinned', labelKey: 'pinned.title', icon: 'pinned' },
      { id: 'specs', to: '/project/specs', labelKey: 'nav.specs', icon: 'specs' },
      { id: 'tasks', to: '/project/tasks', labelKey: 'nav.tasks', icon: 'tasks' },
      // Qualidade is one tab; these three are its views.
      { id: 'audit', to: '/project/quality', labelKey: 'nav.audit', icon: 'audit', tab: 'quality', end: true },
      { id: 'security', to: '/project/quality/security', labelKey: 'nav.security', icon: 'security', tab: 'quality', minRole: 'EngineeringManager' },
    ],
  },
  {
    // Following the work, one screen with views: the floor, the list, sessions, reports, usage, my day.
    id: 'track',
    labelKey: 'nav.area.track',
    icon: 'track',
    to: '/track',
    alsoCovers: ['/work', '/developers'],
    items: [
      // Two rooms, one tab (docs/refactor/25): the floor where everybody works, and the bug room.
      { id: 'live', to: '/track/floor', labelKey: 'nav.room.follow', icon: 'live', tab: 'rooms' },
      { id: 'bugHunt', to: '/track/bugs', labelKey: 'nav.room.bugs', icon: 'bugHunt', tab: 'rooms', bugHunt: true },
      { id: 'activity', to: '/track/list', labelKey: 'nav.activity', icon: 'activity' },
      { id: 'sessions', to: '/track/sessions', labelKey: 'nav.sessions', icon: 'sessions' },
      { id: 'reports', to: '/track/reports', labelKey: 'nav.reports', icon: 'reports' },
      { id: 'usage', to: '/track/usage', labelKey: 'nav.usage', icon: 'usage', minRole: 'EngineeringManager' },
      { id: 'myDay', to: '/track/day', labelKey: 'nav.myDay', icon: 'myDay', developer: true },
    ],
  },
  // Setting things up, once: the organization, the projects linked to it, plugins, prompts. Not in the
  // sidebar, which keeps to the work (agents, project, following it): a menu in the header, by the name.
  {
    id: 'settings',
    labelKey: 'nav.settings',
    icon: 'settings',
    placement: 'header',
    items: [
      // Setting an agent up is configuration, not the day's work (41, A7): connecting one, its skills and MCPs.
      { id: 'connect', to: '/agents/connect', labelKey: 'nav.connectAgent', icon: 'connect' },
      { id: 'skills', to: '/agents/skills', labelKey: 'skills.title', icon: 'skills' },
      { id: 'plugins', to: '/agents/plugins', labelKey: 'nav.plugins', icon: 'plugins' },
      { id: 'prompts', to: '/agents/prompts', labelKey: 'prompts.title', icon: 'prompts' },
      // The projects linked to the organization; the ones open here are in the sidebar's Trabalho.
      { id: 'repositories', to: '/agents/repositories', labelKey: 'nav.repositories', icon: 'repositories' },
      { id: 'organization', to: '/settings', labelKey: 'nav.organization', icon: 'settings', minRole: 'EngineeringManager' },
    ],
  },
]

/** What decides which entries a person sees. */
export interface NavContext {
  role?: Role
  isDeveloper: boolean
  bugHuntEnabled: boolean
}

function allowed(requirement: NavRequirement, context: NavContext): boolean {
  if (requirement.minRole && !atLeast(context.role, requirement.minRole)) return false
  if (requirement.developer && !context.isDeveloper) return false
  if (requirement.bugHunt && !context.bugHuntEnabled) return false
  return true
}

/** The tree as this person sees it: gated items removed, and areas left empty removed with them. */
export function visibleAreas(context: NavContext, areas: NavArea[] = NAV_AREAS): NavArea[] {
  return areas
    .filter((area) => allowed(area, context))
    .map((area) => ({ ...area, items: area.items.filter((item) => allowed(item, context)) }))
    .filter((area) => area.to !== undefined || area.items.length > 0)
}

const under = (pathname: string, route: string): boolean => pathname === route || pathname.startsWith(`${route}/`)

/** The item a path belongs to — the longest route that contains it, so `/docs/pinned` beats `/docs`. */
export function findNavItem(pathname: string, areas: NavArea[] = NAV_AREAS): { area: NavArea; item: NavItem | null } | null {
  let best: { area: NavArea; item: NavItem | null; length: number } | null = null
  for (const area of areas) {
    const candidates: Array<{ route: string; item: NavItem | null }> = [
      ...(area.to ? [{ route: area.to, item: null }] : []),
      ...area.items.map((item) => ({ route: item.to, item })),
    ]
    for (const { route, item } of candidates) {
      if (under(pathname, route) && (!best || route.length > best.length)) best = { area, item, length: route.length }
    }
  }
  return best ? { area: best.area, item: best.item } : null
}

/** The area a path is in, including the paths an area covers without an item of their own. */
export function areaFor(pathname: string, areas: NavArea[] = NAV_AREAS): NavArea | null {
  const found = findNavItem(pathname, areas)
  if (found) return found.area
  return areas.find((area) => area.alsoCovers?.some((route) => under(pathname, route))) ?? null
}

/** Every path an area's folding group in the sidebar should open for. */
export function areaPaths(area: NavArea): string[] {
  return [...(area.to ? [area.to] : []), ...area.items.map((item) => item.to), ...(area.alsoCovers ?? [])]
}

/** The translation key of the page title for a path in the tree, or null for a path outside it. */
export function titleKeyFor(pathname: string, areas: NavArea[] = NAV_AREAS): string | null {
  const found = findNavItem(pathname, areas)
  if (!found) return null
  // In a space with tabs the tab already says where one is: the title is the space.
  if (found.area.to && found.area.items.length > 0) return found.area.labelKey
  return found.item ? found.item.labelKey : found.area.labelKey
}

/** Every translation key the tree uses, for the test that holds the dictionaries to it. */
export function navLabelKeys(areas: NavArea[] = NAV_AREAS): string[] {
  return [...new Set(areas.flatMap((area) => [area.labelKey, ...spaceTabs(area).map((tab) => tab.labelKey), ...area.items.map((item) => item.labelKey)]))]
}

/**
 * The routes before the spaces had tabs (docs/refactor/05-navegacao.md), and where each went. Old
 * links — bookmarks, notifications, documents, devpulse:// — still land on the right tab.
 */
export const MOVED_ROUTES: Record<string, string> = {
  '/docs': '/project/docs',
  '/docs/pinned': '/project/pinned',
  '/sdd': '/project/specs',
  '/tasks': '/project/tasks',
  '/audit': '/project/quality',
  '/bug-hunt': '/track/bugs',
  // The bug room moved from Projeto › Qualidade to Acompanhar › Salas (docs/refactor/25).
  '/project/quality/bug-hunt': '/track/bugs',
  '/security': '/project/quality/security',
  '/editor': '/project/editor',
  '/live': '/track/floor',
  '/activity': '/track/list',
  '/sessions': '/track/sessions',
  '/reports': '/track/reports',
  '/usage': '/track/usage',
  '/me/day': '/track/day',
  '/remote': '/agents/remote',
}

/** A space's tabs as this person sees them: one per item, items sharing a `tab` folded into one. */
export function spaceTabs(area: NavArea): Array<{ id: string; to: string; labelKey: string; views: NavItem[] }> {
  const tabs: Array<{ id: string; to: string; labelKey: string; views: NavItem[] }> = []
  for (const item of area.items) {
    const id = item.tab ?? item.id
    const existing = tabs.find((tab) => tab.id === id)
    if (existing) existing.views.push(item)
    else tabs.push({ id, to: item.to, labelKey: item.tab ? `nav.tab.${item.tab}` : item.labelKey, views: [item] })
  }
  return tabs
}
